import React, { useContext, useEffect, useState } from 'react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ApplyCalendarLayoutAssignmentsOutDTO, CalendarLayoutModelDetailOutDTO, CalendarLayoutModelRowOutDTO } from '@api/ringil-api.v2';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge.tsx';
import { formatDateTimeInTzWithLocale } from '@utils/dateWithTz';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import { CalendarLayoutEditor } from '../editor/CalendarLayoutEditor';
import { ExportImportDialog } from '../editor/ExportImportDialog';
import { ApiContext } from '@api/api';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { InputTextarea } from 'primereact/inputtextarea';
import { CalendarLayoutModelMenu } from './CalendarLayoutModelMenu';
import { payloadFromConfig } from '../payload';
import { useCalendarLayoutModel } from './useCalendarLayoutModel';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import { ApplyModelDialog } from './ApplyModelDialog';
import { ModelAssignmentsTab } from './ModelAssignmentsTab';

/** The two surfaces are separate tabs rather than a switch inside the designer. */
type ModelTab = 'card' | 'detail' | 'assignments';

/** Tab order, so the strip's index-based change event maps back to a name. */
const TAB_ORDER: ModelTab[] = ['card', 'detail', 'assignments'];

interface CalendarLayoutModelEditorProps {
  modelId: string;
  /** Admin mode lets the apply dialog target any company; company mode is fixed to the caller's own. */
  admin: boolean;
  /** Called after every server-side change, so anything the host page shows stays accurate. */
  onChanged?: () => void;
  /** Reports the loaded model, so the page can put its name in the breadcrumb and the heading. */
  onLoaded?: (detail: CalendarLayoutModelDetailOutDTO | null) => void;
  /** Called after the model is deleted from here — the page it lived on no longer exists. */
  onDeleted?: () => void;
  /** Opens a duplicate of this model, once the copy has been created. */
  onOpenModel?: (modelId: string) => void;
}

/**
 * One model's layout, with its draft lifecycle.
 *
 * Drafts are the whole point of the screen: edits are saved privately until published, so a model
 * that a hundred locations already render can be reworked without anyone seeing it half-finished.
 *
 * Page chrome — breadcrumbs, heading, the way back — belongs to the host page, not here.
 */
export const CalendarLayoutModelEditor: React.FC<CalendarLayoutModelEditorProps> = ({ modelId, admin, onChanged, onLoaded, onDeleted, onOpenModel }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const model = useCalendarLayoutModel(modelId, onChanged);

  // The backend decides: it knows the caller's permissions, not just whether the model is shared.
  // False until the detail arrives, so nothing is briefly editable that should not be.
  const editable = !!model.detail?.editable;

  useEffect(() => {
    onLoaded?.(model.detail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.detail]);

  const { createCalendarLayoutModel, updateCalendarLayoutModel, deleteCalendarLayoutModels, archiveCalendarLayoutModels, setDefault } = useContext(ApiContext);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /** Name and description under edit; null when the rename dialog is closed. */
  const [renaming, setRenaming] = useState<{ name: string; description: string } | null>(null);
  const [renamingSaving, setRenamingSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyResults, setApplyResults] = useState<ApplyCalendarLayoutAssignmentsOutDTO | null>(null);
  // Bumped so the assignments tab refetches the moment an assignment is made from this screen.
  const [assignmentsToken, setAssignmentsToken] = useState(0);
  // The tab label's count. Seeded from the model detail (so it is right before the tab is ever
  // opened) and then kept live by the table itself, which knows sooner than a model reload does.
  const [assignmentCount, setAssignmentCount] = useState<number | null>(null);

  // The event card first: it is the surface people come here to change, and the popover is the
  // follow-up. Assignments last, since it is about the model rather than its layout.
  const [tab, setTab] = useState<ModelTab>('card');

  const tabs: TabProps[] = [
    { label: tr('CalendarLayout.tab.card', 'Event card layout'), active: tab === 'card' },
    { label: tr('CalendarLayout.tab.detail', 'Popover layout'), active: tab === 'detail' },
    {
      label: tr('CalendarLayout.tab.assignments', 'Assignments ({{count}})', { count: assignmentCount ?? model.detail?.assignmentCount ?? 0 }),
      active: tab === 'assignments',
    },
  ];

  const timezoneId = loggedUser?.defaultTimezoneId ?? 'Europe/Prague';
  const withWho = (when?: string, who?: string) => [who, formatDateTimeInTzWithLocale(when, timezoneId)].filter(Boolean).join(', ');

  /**
   * Whether what is on screen differs from what calendars render. Deliberately a badge and not a
   * paragraph: it is state of the entity, like every other badge in the row.
   */
  const pendingLabel = model.dirty
    ? tr('CalendarLayout.unsaved', 'Unsaved changes')
    : model.hasDraft
      ? tr('CalendarLayout.draftPending', 'Draft pending')
      : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Title and actions on one row, as on the other detail pages — the actions go through
          `Heading`'s buttons slot rather than a wrapper of our own. That wrapper needed a z-index to
          keep the more menu above the sticky tab strip, and the stacking context it created was the
          reason the menu ended up behind it anyway. SupplierNotificationDetail does it this way. */}
      <Heading
        title={model.detail?.name ?? ''}
        headingVariant={'h1'}
        buttons={[
          editable && (
            <div key="actions" className="flex flex-row items-center gap-2">
              <RingilButton
                label={tr('CalendarLayout.saveDraft', 'Save draft')}
                mode="outlined-rectangle"
                disabled={!model.dirty || model.busy}
                onClick={model.save}
              />
              <RingilButton
                label={tr('CalendarLayout.publish', 'Publish')}
                mode="filled-rectangle"
                disabled={model.busy || (!model.dirty && !model.hasDraft)}
                onClick={model.publish}
              />
              <RingilButton label={tr('CalendarLayout.applyTo', 'Apply to…')} mode="filled-rectangle" onClick={() => setApplyOpen(true)} />
            </div>
          ),
          /* The same secondary actions as the list's row menu, so the two places agree on what a
             model can do — plus export/import, which only exists here. */
          <CalendarLayoutModelMenu
            key="more"
            dataTestId="calendar-layout-model-more"
            editable={editable}
            admin={admin}
            published={!!model.detail?.publishedAt}
            archived={model.detail?.calendarLayoutStatus === 'ARCHIVED'}
            // The list marks which model is the default; from inside one, the action is simply
            // offered whenever the model is published.
            isDefault={false}
            onExportImport={() => setExportOpen(true)}
            draft={{
              onDiscard: model.discard,
              canDiscard: (model.hasDraft || model.dirty) && !model.busy,
              onRevert: model.revert,
              canRevert: !!model.detail?.canRevert && !model.busy,
            }}
            onDuplicate={() => {
              if (!model.detail) return;
              // No dialog here, unlike the list: the source is unambiguous, so the copy is made
              // straight away and opened for editing.
              createCalendarLayoutModel(
                {
                  name: tr('CalendarLayout.copyOf', 'Copy of {{name}}', { name: model.detail.name }),
                  description: model.detail.description,
                  systemWide: model.detail.systemWide,
                  ownerCompanyId: model.detail.ownerCompanyId,
                  // Exactly what is on screen — draft over published, including edits not saved yet.
                  // Anything else copies a layout the author is not looking at.
                  payload: payloadFromConfig(model.config),
                },
                created => onOpenModel?.(created.id),
              );
            }}
            onRename={() => setRenaming({ name: model.detail?.name ?? '', description: model.detail?.description ?? '' })}
            onSetDefault={() => setDefault({ modelId }, () => model.reload())}
            onArchive={() =>
              archiveCalendarLayoutModels({ modelIds: [modelId] }, () => {
                model.reload();
                onChanged?.();
              })
            }
            onDelete={() => setConfirmingDelete(true)}
          />,
        ]}
      />

      {/* Entity metadata as badges, the same as every other detail page: owner, publish state and
          the audit trail, rather than a sentence describing them. */}
      {model.detail && (
        <Ringil3BadgeRow>
          <Badge
            label={tr('CalendarLayout.owner', 'Owner')}
            value={model.detail.systemWide ? tr('CalendarLayout.systemWide', 'System-wide') : (model.detail.ownerCompanyName ?? '—')}
            highlightedValue={model.detail.systemWide}
          />
          {pendingLabel && <Badge className="!bg-content-brandMedium" value={pendingLabel} />}
          <Badge
            label={tr('CalendarLayout.publishedAt', 'Published')}
            value={model.detail.publishedAt ? withWho(model.detail.publishedAt, model.detail.publishedByFullName) : tr('CalendarLayout.notPublished', 'Not published')}
          />
          {model.detail.draftPayload && (
            <Badge label={tr('CalendarLayout.draftSavedAt', 'Draft')} value={withWho(model.detail.draftSavedAt, model.detail.draftSavedByFullName)} />
          )}
          {model.detail.updatedAt && (
            <Badge label={tr('CalendarLayout.updatedAt', 'Updated')} value={withWho(model.detail.updatedAt, model.detail.updatedByFullName)} />
          )}
          <CreatedBadge createdAt={model.detail.createdAt} createdBy={model.detail.createdByFullName} timezoneId={timezoneId} />
        </Ringil3BadgeRow>
      )}

      {/* No `anchorId`s: these are real panes, not sections of one scrolling page, so the strip acts
          as a plain tab bar and the selection is owned here. */}
      <Tabs tabs={tabs} onTabChange={index => setTab(TAB_ORDER[index])} />

      {/* One editor for both surface tabs rather than two: the draft, the busy state and the
          save/publish actions belong to the model, not to a surface, and remounting per tab would
          drop the selected fragment every time you compared card against popover. */}
      <div className={tab === 'assignments' ? 'hidden' : 'contents'}>
      <CalendarLayoutEditor
        value={model.config}
        onChange={model.change}
        surface={tab === 'detail' ? 'detail' : 'card'}
        readOnly={!editable}
      />
      </div>

      {tab === 'assignments' && (
        <ModelAssignmentsTab
          modelId={modelId}
          admin={admin}
          editable={editable}
          refreshToken={assignmentsToken}
          onTotalChange={setAssignmentCount}
          onChanged={() => {
            // Refresh the header count as well as the caller's list.
            model.reload();
            onChanged?.();
          }}
        />
      )}

      {/* Rename, reachable without going back to the list. The heading and breadcrumb pick the new
          name up through the reload's `onLoaded`. */}
      <RingilDialog
        visible={renaming !== null}
        onHide={() => setRenaming(null)}
        heading={tr('CalendarLayout.rename', 'Rename')}
        buttons={
          <>
            <RingilButton
              label={tr('CalendarLayout.save', 'Save')}
              mode="filled"
              disabled={renamingSaving || !renaming?.name.trim()}
              onClick={() => {
                if (!renaming?.name.trim() || !model.detail) return;
                setRenamingSaving(true);
                updateCalendarLayoutModel(
                  modelId,
                  {
                    name: renaming.name.trim(),
                    description: renaming.description.trim() || undefined,
                    calendarLayoutStatus: model.detail.calendarLayoutStatus,
                  },
                  () => {
                    setRenamingSaving(false);
                    setRenaming(null);
                    model.reload();
                    onChanged?.();
                  },
                  undefined,
                  { onError: (_error, showStandardError) => { setRenamingSaving(false); showStandardError(); } },
                );
              }}
            />
            <RingilButton label={tr('CalendarLayout.cancel', 'Cancel')} mode="outlined" onClick={() => setRenaming(null)} />
          </>
        }
      >
        <div className="flex flex-col gap-3 w-[480px]">
          <Field label={tr('CalendarLayout.modelName', 'Name')} required={!renaming?.name.trim()}>
            <RingilInputText value={renaming?.name ?? ''} onChange={value => setRenaming(previous => previous && { ...previous, name: value })} fullWidth />
          </Field>
          <Field label={tr('CalendarLayout.modelDescription', 'Description')}>
            <InputTextarea
              value={renaming?.description ?? ''}
              onChange={e => setRenaming(previous => previous && { ...previous, description: e.target.value })}
              rows={3}
              className="w-full"
            />
          </Field>
        </div>
      </RingilDialog>

      <ExportImportDialog
        visible={exportOpen}
        value={model.config}
        readOnly={!editable}
        onHide={() => setExportOpen(false)}
        onImport={model.change}
      />

      {/* Deleting from the detail page also removes its assignments, and takes the page with it. */}
      <ConfirmDialog
        visible={confirmingDelete}
        dialogTitle={tr('CalendarLayout.deleteHeading', 'Delete layout model')}
        dialogMessage={
          model.detail?.assignmentCount
            ? tr('CalendarLayout.deleteAssignedWarning', '“{{name}}” is applied to {{count}} scope(s). Deleting it removes those assignments, and each scope falls back to whatever it inherits. This cannot be undone.', {
                name: model.detail?.name ?? '',
                count: model.detail?.assignmentCount ?? 0,
              })
            : tr('CalendarLayout.deleteConfirm', 'Delete “{{name}}”? This cannot be undone.', { name: model.detail?.name ?? '' })
        }
        onAcceptButtonText={tr('CalendarLayout.delete', 'Delete')}
        onCancelButtonText={tr('CalendarLayout.cancel', 'Cancel')}
        loading={model.busy}
        onConfirm={() =>
          deleteCalendarLayoutModels({ modelIds: [modelId] }, () => {
            setConfirmingDelete(false);
            onChanged?.();
            onDeleted?.();
          })
        }
        onCancel={() => setConfirmingDelete(false)}
      />

      <ApplyModelDialog
        // The dialog is driven by its `model` prop being non-null, so the open flag gates it here.
        model={applyOpen && model.detail ? ({ ...model.detail, published: !!model.detail.publishedAt } as CalendarLayoutModelRowOutDTO) : null}
        admin={admin}
        onHide={() => setApplyOpen(false)}
        onApplied={results => {
          setApplyResults(results);
          setAssignmentsToken(token => token + 1);
          model.reload();
          onChanged?.();
        }}
      />

      {/* Per-target outcome, same as the assignment screens — a skip has to be visible. */}
      <RingilDialog
        visible={applyResults !== null}
        onHide={() => setApplyResults(null)}
        heading={tr('CalendarLayout.assignResults', 'Result')}
        buttons={<RingilButton label={tr('CalendarLayout.close', 'Close')} mode="filled" onClick={() => setApplyResults(null)} />}
      >
        <div className="flex flex-col gap-1 w-[520px] max-h-[60vh] overflow-y-auto text-xs">
          {(applyResults?.results ?? []).map((result, index) => (
            <div
              key={`${result.companyId}:${result.locationId ?? ''}:${index}`}
              className="flex flex-row justify-between gap-3 border-b border-0 border-solid border-content-quaternary py-1"
            >
              <span>{[result.companyId, result.locationId].filter(Boolean).join(' — ')}</span>
              <span className={result.calendarLayoutOutcome === 'SKIPPED' ? 'text-content-tertiary' : 'text-content-primary'}>
                {result.calendarLayoutOutcome}
                {result.reason ? ` — ${result.reason}` : ''}
              </span>
            </div>
          ))}
        </div>
      </RingilDialog>
    </div>
  );
};
